/**
 * ServiceAreaStormsWidget — Dashboard tile showing the most-active storm
 * areas in the contractor's home state over the last 90 days.
 *
 * Hits /api/storms/service-area which automatically resolves the org's state
 * (from billing address OR most-recent quote). Surfaces the top 5 storm
 * "cells" with the count of confirmed events + worst-case hail/wind so the
 * contractor knows where to go knock.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudHail, MapPin, ArrowRight, Loader2, Wind } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

export function ServiceAreaStormsWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get('/storms/service-area?days_back=90&top_n=5')
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setData({ top_areas: [], total_events: 0 }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <CloudHail className="h-4 w-4 text-sky-600" /> Recent storms in your service area
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading storm activity…
                </CardContent>
            </Card>
        );
    }

    const areas = data?.top_areas || [];
    const total = data?.total_events || 0;
    const state = data?.state;

    if (!state) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <CloudHail className="h-4 w-4 text-slate-400" /> Recent storms in your service area
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-slate-500">
                    {data?.note || 'Build your first quote to anchor your service area, then we\'ll show storm activity here.'}
                </CardContent>
            </Card>
        );
    }

    if (areas.length === 0) {
        return (
            <Card className="border-emerald-100 bg-emerald-50/40">
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <CloudHail className="h-4 w-4 text-emerald-600" /> Recent storms in {state}
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-slate-600">
                    No significant severe weather in {state} over the last 90 days. Quiet times — focus on referrals and finishing existing jobs.
                </CardContent>
            </Card>
        );
    }

    return (
        <Card data-testid="dashboard-service-area-storms">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <CloudHail className="h-4 w-4 text-sky-600" /> Recent storms in {state}
                    </CardTitle>
                    <Badge variant="outline" className="text-[10px] font-normal bg-amber-50 border-amber-200 text-amber-800">
                        Last 90 days · {total} events
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
                {areas.map((a, i) => (
                    <div
                        key={a.cell}
                        className="flex items-start justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50"
                        data-testid={`storm-area-${i}`}
                    >
                        <div className="flex items-start gap-2 min-w-0">
                            <div className="h-6 w-6 rounded bg-sky-100 text-sky-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                                {i + 1}
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-900 truncate">
                                    {a.cities && a.cities.length > 0 ? a.cities.join(', ') : `${a.lat}, ${a.lng}`}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                    <span className="inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" /> {a.lat}, {a.lng}</span>
                                    {a.max_hail_in > 0 && <span className="inline-flex items-center gap-0.5"><CloudHail className="h-2.5 w-2.5" /> {a.max_hail_in}&quot;</span>}
                                    {a.max_wind_mph > 0 && <span className="inline-flex items-center gap-0.5"><Wind className="h-2.5 w-2.5" /> {a.max_wind_mph}mph</span>}
                                </div>
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-display text-lg text-slate-900 tabular-nums">{a.count}</div>
                            <div className="text-[9px] text-slate-500 uppercase tracking-wider">events</div>
                        </div>
                    </div>
                ))}
                <Link
                    to="/app/leads"
                    className="block mt-2 text-xs text-amber-700 hover:text-amber-800 font-semibold inline-flex items-center gap-1"
                    data-testid="dashboard-storms-cta"
                >
                    Knock these neighborhoods <ArrowRight className="h-3 w-3" />
                </Link>
            </CardContent>
        </Card>
    );
}
